import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { FooterActionComponent } from '@shared/components/footer-action/footer-action.component';
import { FormulaCardComponent } from '@shared/components/formula/formula-card/formula-card.component';
import { FormulaEditorDialogComponent, FormulaEditorDialogData, FormulaEditorDialogResult } from '@shared/components/formula/formula-editor-dialog/formula-editor-dialog.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { FuncionalidadeEnum } from '@shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '@shared/enums/usuario/modulo.enum';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import { TipoArredondamentoEnum } from '@shared/enums/formula/tipo-arredondamento.enum';
import { TipoFormulaEnum } from '@shared/enums/formula/tipo-formula.enum';
import { TipoOrigemVariavelEnum } from '@shared/enums/formula/tipo-origem-variavel.enum';
import { SecaoFormulaCustoVendaEnum } from '@shared/enums/suprimentos/secao-formula-custo-venda.enum';
import { ConfiguracaoCustoVendaService } from '@shared/services/apis/suprimentos/configuracao-custo-venda.service';
import { FormulaCatalogoService } from '@shared/services/apis/suprimentos/formula-catalogo.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { MaskService } from '@shared/services/mask.service';
import { FormulaCatalogoItemStruct } from '@shared/services/structs/formula/formula-catalogo-item.struct';
import { FormulaItemStruct } from '@shared/services/structs/formula/formula-item.struct';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';
import { classeBadgeVariavel, FORMULA_BADGE_CLASSES, isVariavelParametro } from '@shared/components/formula/utils/formula-badge.util';

import { FormulaCatalogoContextoStruct } from '@shared/services/structs/formula/formula-catalogo-contexto.struct';
import { FormulaCatalogoFormulaStruct } from '@shared/services/structs/formula/formula-catalogo-formula.struct';
import { FormulaCatalogoVariavelStruct } from '@shared/services/structs/formula/formula-catalogo-variavel.struct';
import { FormulaSimulacaoDialogComponent, FormulaSimulacaoDialogData } from '@shared/components/formula/formula-simulacao-dialog/formula-simulacao-dialog.component';
import { ApiMotorCalculoEnum } from '@shared/enums/formula/api-motor-calculo.enum';
import { FormulaErrorCodeEnum } from '@shared/enums/formula/formula-error-code.enum';
import { FormulaCicloDetectadoStruct } from '@shared/services/structs/formula/formula-ciclo-detectado.struct';

@Component({
  selector: 'app-formulas-custo-venda',
  standalone: true,
  imports: [CommonModule, FormsModule, MatBadgeModule, MatButtonModule, MatExpansionModule, MatFormFieldModule, MatIconModule, MatInputModule, MatTooltipModule, NgxCurrencyDirective, FooterActionComponent, FormulaCardComponent, HeaderComponent, LoadingBoxComponent],
  templateUrl: './formulas-custo-venda.component.html',
  styleUrls: ['./formulas-custo-venda.component.css'],
})
export class FormulasCustoVendaComponent implements OnInit {
  public readonly Secao = SecaoFormulaCustoVendaEnum;
  public readonly badgeClasses = FORMULA_BADGE_CLASSES;
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Suprimentos;
  public formulas: FormulaCatalogoFormulaStruct[] = [];
  public contextos: FormulaCatalogoContextoStruct[] = [];
  public markupPadrao: number | null = null;
  public readonly percentMask = this.maskService.getPercentMask(2);
  public isLoading = true;
  public isLoadingSalvar = false;
  public podeAlterar = false;
  public catalogoIndisponivel = false;
  public configuracaoIndisponivel = false;
  private snapshotFormulas: FormulaCatalogoFormulaStruct[] = [];
  private snapshotMarkup: number | null = null;
  public expandidas = new Set<string>();

  constructor(
    private service: ConfiguracaoCustoVendaService,
    private formulaCatalogoService: FormulaCatalogoService,
    private maskService: MaskService,
    private authService: AuthService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private router: Router,
  ) {}

  ngOnInit(): void {
    const usuario = this.authService.getUserInfos();
    this.podeAlterar = usuario.listaPermissoes.includes(PermissaoEnum.Configuracoes_Suprimentos_Alterar);
    this.carregar();
  }

  public listarFormulas(contexto: FormulaCatalogoContextoStruct): FormulaCatalogoFormulaStruct[] {
    const formulas = [...contexto.formulas, ...contexto.filhos.flatMap(filho => this.listarFormulas(filho))];
    return [...new Map(formulas.map(item => [item.chaveFormula, this.formulas.find(atual => atual.chaveFormula === item.chaveFormula) ?? item])).values()];
  }
  public contarPersonalizadas(contexto: FormulaCatalogoContextoStruct): number { return this.listarFormulas(contexto).filter(x => x.configuracaoEfetiva.isPersonalizada).length; }
  public possuiFormulas(contexto: FormulaCatalogoContextoStruct): boolean { return this.listarFormulas(contexto).length > 0; }
  public isExpandida(chaveFormula: string): boolean { return this.expandidas.has(chaveFormula); }
  public expandir(chaveFormula: string): void { this.expandidas.add(chaveFormula); }
  public recolher(chaveFormula: string): void { this.expandidas.delete(chaveFormula); }
  public ehSecaoPrecoVenda(contexto: FormulaCatalogoContextoStruct): boolean { return this.obterSecao(contexto) === this.Secao.PrecoVenda; }
  public nomeSecao(contexto: FormulaCatalogoContextoStruct): string { return this.ehSecaoPrecoVenda(contexto) ? 'Preço de venda' : 'Preço de custo'; }
  public iconeSecao(contexto: FormulaCatalogoContextoStruct): string { return this.ehSecaoPrecoVenda(contexto) ? 'sell' : 'request_quote'; }
  public descricoesSecao(contexto: FormulaCatalogoContextoStruct): string[] {
    return this.ehSecaoPrecoVenda(contexto)
      ? ['O <strong>preço de venda</strong> parte do preço de custo e recebe o markup — o percentual aplicado sobre o custo para formar o valor de venda.', 'Se precisar de outra forma de cálculo, use <strong>Personalizar fórmulas</strong>.']
      : ['O <strong>preço de custo</strong> é quanto o produto realmente custa para entrar no estoque: valor da mercadoria, frete, seguro, despesas e impostos não recuperáveis, menos descontos e impostos recuperáveis.', 'O sistema já vem com o cálculo pronto. Se sua empresa calcula de outra forma, use <strong>Personalizar fórmulas</strong>.'];
  }
  public classeVariavel(formula: FormulaCatalogoFormulaStruct): string {
    return classeBadgeVariavel(this.mapearVariavel(formula.variavelSaida));
  }
  public isParametro(formula: FormulaCatalogoFormulaStruct): boolean {
    return isVariavelParametro(this.mapearVariavel(formula.variavelSaida));
  }

  public mapearFormulaCard(formula: FormulaCatalogoFormulaStruct, isPersonalizavel = false): FormulaCatalogoItemStruct {
    return {
      chaveFormula: formula.chaveFormula,
      descricao: formula.descricao,
      isPersonalizavel: isPersonalizavel && formula.isPersonalizavel,
      variavelPrincipal: this.mapearVariavel(formula.variavelSaida),
      variaveis: formula.variaveisPermitidas.map(x => this.mapearVariavel(x)),
      variaveisPermitidas: formula.variaveisPermitidas.map(x => this.mapearVariavel(x)),
      modelos: formula.modelos.map(modelo => ({
        chaveFormulaModelo: modelo.chaveFormulaModelo,
        modelo: modelo.modelo,
        nome: modelo.nome,
        descricao: modelo.descricao,
        expressao: modelo.expressao,
        tipoArredondamento: modelo.tipoArredondamento,
        casasDecimais: modelo.casasDecimais,
        isModeloPadraoSistema: modelo.isModeloPadraoSistema,
        dataVigenciaInicio: modelo.dataVigenciaInicio,
        dataVigenciaFim: modelo.dataVigenciaFim,
      })),
      chaveFormulaModeloEmpresa: formula.configuracaoEfetiva.chaveFormulaModelo,
    };
  }

  public personalizar(contexto: FormulaCatalogoContextoStruct): void {
    const formulas = this.listarFormulas(contexto);
    if (!this.podeAlterar || this.catalogoIndisponivel || this.configuracaoIndisponivel || formulas.length === 0) return;
    const data: FormulaEditorDialogData = {
      titulo: contexto.nome,
      formulas: formulas.map(x => this.mapearFormulaCard(x, true)),
      estadosIniciais: formulas.map(x => this.mapearEstadoFormula(x)),
      contextoRaiz: 'custo-venda',
      validarPersonalizacoes: request => {
        const porChave = new Map(request.formulas.map(item => [item.chaveFormula, item]));
        request.criterios = {};
        request.formulas = this.mapearPersonalizacoes().map(item => {
          const alteracao = porChave.get(item.chaveFormula);
          return {
            chaveFormula: item.chaveFormula,
            chaveFormulaModelo: alteracao
              ? alteracao.chaveFormulaModelo
              : item.tipo === TipoFormulaEnum.ModeloAlternativo ? item.chaveFormulaModelo ?? null : null,
            expressaoPersonalizada: alteracao
              ? alteracao.expressaoPersonalizada
              : item.tipo === TipoFormulaEnum.Personalizada ? item.expressao ?? '' : null,
          };
        });
        return this.formulaCatalogoService.validarPersonalizacoes(request);
      },
      mostrarVigencia: true,
      mostrarBadgeModelo: false,
      mensagemAlerta: 'A personalização altera como o sistema calcula esta configuração. Confira as expressões antes de confirmar — a responsabilidade pelo cálculo customizado é do usuário.',
      // Custo/venda não tem ciclo configurável: referência circular é inválida e só gera o aviso.
      permitirConfigurarCiclo: false,
    };
    this.dialog.open(FormulaEditorDialogComponent, {
      width: '900px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      disableClose: true,
      data,
    })
      .afterClosed().subscribe((result: FormulaEditorDialogResult | undefined) => {
        if (!result) return;
        const estados = new Map(result.formulas.map(x => [x.chaveFormula, x]));
        this.formulas = this.formulas.map(formula => {
          const estado = estados.get(formula.chaveFormula);
          if (!estado) return formula;
          return estado.tipo === TipoFormulaEnum.Personalizada
            ? { ...formula, configuracaoEfetiva: { ...formula.configuracaoEfetiva, chaveFormulaModelo: estado.chaveFormulaModelo ?? formula.configuracaoEfetiva.chaveFormulaModelo, isPersonalizada: true, expressaoVigente: estado.expressao ?? formula.configuracaoEfetiva.expressaoPadrao, tipoArredondamento: estado.tipoArredondamento ?? TipoArredondamentoEnum.Arredondar, casasDecimais: estado.casasDecimais ?? 2, dataVigenciaInicio: estado.dataVigenciaInicio ?? formula.configuracaoEfetiva.dataVigenciaInicio, dataVigenciaFim: estado.dataVigenciaFim ?? null } }
            : { ...formula, configuracaoEfetiva: { ...formula.configuracaoEfetiva, chaveFormulaModelo: estado.chaveFormulaModelo ?? formula.configuracaoEfetiva.chaveFormulaModelo, isPersonalizada: false, expressaoVigente: formula.configuracaoEfetiva.expressaoPadrao, tipoArredondamento: TipoArredondamentoEnum.Arredondar, casasDecimais: 2, dataVigenciaInicio: null, dataVigenciaFim: null } };
        });
      });
  }

  public simular(contexto: FormulaCatalogoContextoStruct): void {
    if (!this.possuiFormulas(contexto)) return;
    const formulas = this.listarFormulas(contexto);
    const isPrecoVenda = this.ehSecaoPrecoVenda(contexto);
    const data: FormulaSimulacaoDialogData = {
      titulo: `Simulação do ${this.nomeSecao(contexto).toLowerCase()}`,
      subtitulo: isPrecoVenda ? 'A partir do custo e do markup' : 'Inclui os impostos recuperáveis',
      api: ApiMotorCalculoEnum.Suprimentos,
      // A seção é um contexto filho de custo-venda; simular só ela faz o preço de custo virar entrada da venda.
      contextoRaiz: contexto.chave,
      formulasCatalogo: formulas.map(x => this.mapearFormulaCard(x)),
      estados: formulas.map(x => this.mapearEstadoFormula(x)),
      entradasIniciais: isPrecoVenda
        ? [{ chaveVariavel: 'venda.item.p_markup', valor: Number(this.markupPadrao || 0) }]
        : [
            { chaveVariavel: 'nfe.infnfe.det.prod.qcom', valor: 1 },
            { chaveVariavel: 'nfe.infnfe.det.prod.vprod', valor: 100 },
          ],
    };
    this.dialog.open(FormulaSimulacaoDialogComponent, { width: '900px', maxWidth: '96vw', maxHeight: '92vh', data });
  }

  public salvar(): void {
    if (!this.podeAlterar || this.isLoadingSalvar || this.catalogoIndisponivel || this.configuracaoIndisponivel) return;
    this.isLoadingSalvar = true;
    this.service.put({ markupPadrao: Number(this.markupPadrao || 0), formulas: this.mapearPersonalizacoes() }).subscribe({
      next: response => {
        this.isLoadingSalvar = false;
        if (response.isError) {
          const erros = response.erros ?? [];
          const erroCiclo = erros.find(item => item.codigo === FormulaErrorCodeEnum.ReferenciaCircular && item.ciclo?.membros?.length);
          if (erroCiclo?.ciclo) {
            this.alertService.show('Referência circular', this.montarMensagemCiclo(erroCiclo.ciclo), AlertType.warning);
            return;
          }
          const mensagem = erros.map(item => item.mensagem).filter(Boolean).join('\n')
            || response.errorDescription || 'Não foi possível salvar as fórmulas.';
          this.alertService.show('Erro', mensagem, AlertType.error);
          return;
        }
        this.snapshotFormulas = structuredClone(this.formulas);
        this.snapshotMarkup = this.markupPadrao;
        this.alertService.show('Sucesso', 'Fórmulas de custo e venda salvas com sucesso.', AlertType.success);
        this.router.navigate(['/configuracoes/suprimentos']);
      },
      error: () => {
        this.isLoadingSalvar = false;
        this.alertService.show('Erro', 'Não foi possível salvar as fórmulas.', AlertType.error);
      },
    });
  }

  /** Mesmo aviso do editor: uma linha com as descrições (até 3) das fórmulas do ciclo, sem chaves técnicas. */
  private montarMensagemCiclo(ciclo: FormulaCicloDetectadoStruct): string {
    const descricoes = [...new Set(ciclo.membros.map(membro => membro.origem?.descricaoFormula).filter(Boolean))].slice(0, 3);
    const lista = descricoes.length > 1 ? ` (${descricoes.slice(0, -1).join(', ')} e ${descricoes[descricoes.length - 1]})` : '';
    return `As fórmulas de custo e venda dependem umas das outras${lista}. Ajuste uma das expressões.`;
  }

  public cancelar(): void {
    this.formulas = structuredClone(this.snapshotFormulas);
    this.markupPadrao = this.snapshotMarkup;
    this.router.navigate(['/configuracoes/suprimentos']);
  }

  public restaurarPadrao(): void {
    if (!this.podeAlterar || this.catalogoIndisponivel || this.configuracaoIndisponivel) return;
    this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Restaurar fórmulas padrão?',
        description: 'As personalizações ainda não salvas serão descartadas e todas as fórmulas voltarão ao modelo do sistema.',
        buttonConfirm: 'Restaurar fórmulas',
        buttonClose: 'Cancelar',
      },
    }).afterClosed().subscribe(result => {
      if (!result?.confirm) return;
      this.formulas = this.formulas.map(x => ({ ...x, configuracaoEfetiva: { ...x.configuracaoEfetiva, isPersonalizada: false, expressaoVigente: x.configuracaoEfetiva.expressaoPadrao, tipoArredondamento: TipoArredondamentoEnum.Arredondar, casasDecimais: 2 } }));
      this.markupPadrao = null;
    });
  }

  private carregar(): void {
    forkJoin({
      configuracao: this.service.get().pipe(catchError(() => of(null))),
      catalogo: this.formulaCatalogoService.getContexto('custo-venda').pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ configuracao, catalogo }) => {
        this.isLoading = false;
        this.formulas = [];
        this.catalogoIndisponivel = !catalogo || catalogo.isError || !catalogo.contexto;
        this.configuracaoIndisponivel = !configuracao || configuracao.isError;
        this.contextos = !this.catalogoIndisponivel && catalogo?.contexto ? [catalogo.contexto] : [];
        this.formulas = this.contextos.flatMap(contexto => this.listarFormulas(contexto));
        if (catalogo?.contexto && catalogo.contexto.formulas.length === 0) this.contextos = catalogo.contexto.filhos;
        if (this.formulas.length === 0) this.catalogoIndisponivel = true;
        this.markupPadrao = configuracao && !configuracao.isError ? configuracao.markupPadrao || null : null;
        if (this.catalogoIndisponivel || this.configuracaoIndisponivel) {
          this.alertService.show('Erro', 'Não foi possível carregar as fórmulas de custo e venda.', AlertType.error);
        }
        this.snapshotFormulas = structuredClone(this.formulas);
        this.snapshotMarkup = this.markupPadrao;
      },
      error: () => {
        this.isLoading = false;
        this.catalogoIndisponivel = true;
        this.configuracaoIndisponivel = true;
        this.alertService.show('Erro', 'Não foi possível carregar as fórmulas.', AlertType.error);
      },
    });
  }

  private mapearVariavel(variavel: FormulaCatalogoVariavelStruct): VariavelStruct {
    return {
      idVariavel: variavel.idVariavel,
      nome: variavel.tipoOrigemVariavel === TipoOrigemVariavelEnum.Sistema
        || variavel.tipoOrigemVariavel === TipoOrigemVariavelEnum.Parametro
        ? variavel.nome.toUpperCase()
        : variavel.nome,
      chave: variavel.chave,
      pathTagXml: variavel.pathTagXml,
      descricao: variavel.descricao,
      observacao: variavel.observacao,
      tipoDadoVariavel: variavel.tipoDadoVariavel,
      tipoEscopoVariavel: variavel.tipoEscopoVariavel,
      tipoOrigemVariavel: variavel.tipoOrigemVariavel,
      isVariavelGrupo: variavel.isVariavelGrupo,
      possuiFormulaAplicavel: variavel.possuiFormulaAplicavel,
    };
  }

  private mapearEstadoFormula(formula: FormulaCatalogoFormulaStruct): FormulaItemStruct {
    if (!formula.configuracaoEfetiva.isPersonalizada) {
      const chavePadraoSistema = formula.modelos.find(item => item.isModeloPadraoSistema)?.chaveFormulaModelo ?? null;
      const chaveSelecionada = formula.configuracaoEfetiva.chaveFormulaModelo ?? chavePadraoSistema;
      return {
        chaveFormula: formula.chaveFormula,
        // Id da personalização vigente (se houver): a API a desativa ao voltar para modelo.
        idFormulaPersonalizada: formula.configuracaoEfetiva.idFormulaPersonalizada ?? null,
        chaveFormulaModelo: chaveSelecionada,
        tipo: chaveSelecionada && chaveSelecionada !== chavePadraoSistema
          ? TipoFormulaEnum.ModeloAlternativo
          : TipoFormulaEnum.ModeloPadrao,
      };
    }
    return {
      chaveFormula: formula.chaveFormula,
      // Id da personalização vigente: a API atualiza a mesma linha em vez de criar outra.
      idFormulaPersonalizada: formula.configuracaoEfetiva.idFormulaPersonalizada ?? null,
      chaveFormulaModelo: formula.configuracaoEfetiva.chaveFormulaModelo,
      tipo: TipoFormulaEnum.Personalizada,
      expressao: formula.configuracaoEfetiva.expressaoVigente,
      tipoArredondamento: formula.configuracaoEfetiva.tipoArredondamento,
      casasDecimais: formula.configuracaoEfetiva.casasDecimais,
      dataVigenciaInicio: formula.configuracaoEfetiva.dataVigenciaInicio,
      dataVigenciaFim: formula.configuracaoEfetiva.dataVigenciaFim,
    };
  }

  private mapearPersonalizacoes(): FormulaItemStruct[] {
    return this.formulas.map(x => this.mapearEstadoFormula(x));
  }

  private obterSecao(contexto: FormulaCatalogoContextoStruct): SecaoFormulaCustoVendaEnum {
    return contexto.chave.toLowerCase().endsWith('.preco-venda')
      ? SecaoFormulaCustoVendaEnum.PrecoVenda
      : SecaoFormulaCustoVendaEnum.PrecoCusto;
  }
}
